import { forwardRef, type HTMLProps } from 'react';

import { Checkbox } from '~/components/Checkbox';
import { cn } from '~/lib/cn';

type CheckboxDropdownItemProps = {
  isCheckbox: true;
  disabled?: boolean;
  checked?: boolean;
  onChange?: (newValue: boolean) => void;
};

type NotCheckboxDropdownItemProps = {
  isCheckbox?: false;
};

type DropdownItemProps = HTMLProps<HTMLDivElement> &
  (CheckboxDropdownItemProps | NotCheckboxDropdownItemProps);

function DropdownItemWithoutRef(
  {
    isCheckbox,
    checked,
    children,
    className,
    disabled,
    ...props
  }: DropdownItemProps,
  ref: React.ForwardedRef<HTMLDivElement>,
) {
  return (
    <div
      role="option"
      tabIndex={0}
      aria-selected={checked}
      className={cn([
        'flex',
        'px-md',
        'py-sm',
        'text-white',
        'gap-sm',
        'transition-colors',
        'duration-200',
        'hover:bg-level-03',
        'focus:bg-level-03',
        'focus:outline-none',
        {
          'cursor-pointer': !disabled,
          'cursor-not-allowed': disabled,
          'text-disabled': disabled,
        },
        className,
      ])}
      aria-disabled={disabled}
      {...props}
      ref={ref}
    >
      {isCheckbox ? (
        <Checkbox
          checked={Boolean(checked)}
          disabled={disabled}
          value="false"
          tabIndex={-1}
        />
      ) : null}
      {children}
    </div>
  );
}

DropdownItemWithoutRef.displayName = 'DropdownItem';

export const DropdownItem = forwardRef(DropdownItemWithoutRef);
